/**
 * Deleting one uploaded file.
 *
 * Two-step, and the second step says what will happen rather than asking the
 * user to be brave: there is no trash and no undo, so the confirmation names
 * the file and states the irreversibility.
 *
 * **The row disappears only after the server says it is gone.** Removing it
 * optimistically would show a file as deleted that a reconstruction is holding
 * on to - which is a refusal the backend makes on purpose, and the user needs
 * to see it.
 */

import { useState } from "react";

import { deleteMedia } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { MediaAsset } from "@/api/types";
import { Button } from "@/components/ui";
// The same sentences the bulk delete uses. One file and twenty files must not
// explain the same refusal differently.
import { deleteFailureText } from "./bulkMedia";

export function DeleteMediaButton({
  asset,
  onDeleted,
}: {
  asset: MediaAsset;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteMedia(asset.id);
      // Only now: the list is reloaded from the server rather than patched.
      onDeleted();
    } catch (caught) {
      setError(
        deleteFailureText(
          caught instanceof ApiError
            ? caught
            : new ApiError({ kind: "unknown", url: "", cause: caught }),
        ),
      );
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <div className="stack-tight">
        <Button onClick={() => setConfirming(true)}>Dosyayı sil</Button>
        {error && (
          <p className="muted" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="stack-tight" role="group" aria-label="Silme onayı">
      <p className="muted">
        <strong>{asset.original_filename}</strong> projeden kaldırılacak. Bu işlem geri
        alınamaz. Bu dosyaya bağlı kalite ve kare seçimi sonuçları da silinir; proje
        kapsaması yeniden hesaplanmalıdır.
      </p>
      <div className="row">
        <Button variant="primary" onClick={remove} busy={busy}>
          Sil
        </Button>
        <Button onClick={() => setConfirming(false)} disabled={busy}>
          Vazgeç
        </Button>
      </div>
    </div>
  );
}
